import { LocalDate } from './local-date'
import { DateTime } from 'luxon'
import { DateTimeUnit } from 'luxon/src/datetime'
import { Temporal } from './temporal'
import { DateFormat, DateTimeFormat, DateUnit, LocalizedDateTimeFormat } from './model/date'
import { LocalTime } from './local-time'
import { Nullable } from '../typesUtils'
import { assertNotNullOrUndefined } from '../tsUtils'

/**
 * Class correspondant à une Date dans la locale française. <br>
 * Elle permet de facilement accéder aux méthodes utilitaires (ie : comparaison, formattage)
 */
export class LocalDateTime extends Temporal {
	private static CURRENT_ZONE = DateTime.now().zone

	public static now = (): LocalDateTime => new LocalDateTime(DateTime.now().setZone(Temporal.API_TIMEZONE))

	static epoch = () => new LocalDateTime(DateTime.fromSeconds(0, {zone: Temporal.API_TIMEZONE}));

	/**
	 * Convertisseur de date reçue par l'API et traitée en front
	 * @param temporal
	 */
	public static fromAPI(temporal: string): LocalDateTime
	public static fromAPI(temporal: undefined): undefined
	public static fromAPI(temporal: null): null
	public static fromAPI(temporal: string | undefined): LocalDateTime | undefined
	public static fromAPI(temporal: string | null): LocalDateTime | null
	public static fromAPI(temporal: Nullable<string>): Nullable<LocalDateTime>
	public static fromAPI(temporal: Nullable<string>): Nullable<LocalDateTime> {
		if (assertNotNullOrUndefined(temporal)) {
			return new LocalDateTime(DateTime.fromISO(temporal, { zone: Temporal.API_TIMEZONE }))
		}
		return temporal
	}

	/**
	 * Convertisseur de date reçue par l'API et traitée en front
	 * Dans le cas des temps de tâche, on conserve la timezone locale et ne traduit pas via UTC
	 * @param temporal
	 */
	public static fromAPIKeepLocalTime(temporal: string): LocalDateTime {
		if (assertNotNullOrUndefined(temporal)) {
			return new LocalDateTime(DateTime.fromISO(temporal, { zone: Temporal.TIMEZONE }))
		}
		return temporal
	}

	static fromAPINotNullOrEpoch(temporal: string) {
		return this.fromAPI(temporal) ?? this.epoch()
	}

	public static fromJS = (date: Date): LocalDateTime => new LocalDateTime(DateTime.fromJSDate(date, { zone: Temporal.API_TIMEZONE }))

	/**
	 * Transformation du time local avant soumission à l'API
	 * Dans le cas des heures de tâche, on conserve la timezone locale
	 * @param temporal
	 */
	public static toApiDateTime = (date: Date): DateTime => DateTime.fromJSDate(date).setZone('utc', { keepLocalTime: true })

	/**
	 * Formatter des datetimes
	 */
	public format(): string
	public format(dateFormat: DateTimeFormat): string
	public format(dateFormat: LocalizedDateTimeFormat = DateFormat.DATETIME_SHORT_WITH_SECONDS, locale?: string): string {
		return this.toDisplayDate(dateFormat, locale)
	}

	/**
	 * Passage du datetime à la fin de la journée
	 */
	public endOfDay = () => new LocalDateTime(this._date.endOf('day'))

	/**
	 * Passage du datetime au début de la journée
	 */
	public startOfDay = () => new LocalDateTime(this._date.startOf('day'))

	/**
	 * Renvoi de la date du datetime
	 */
	public get date(): LocalDate {
		return LocalDate.fromJS(this._date.toJSDate())
	}

	/**
	 * Renvoi du time du datetime
	 */
	public get time(): LocalTime {
		return LocalTime.fromJS(this._date.toJSDate())
	}

	/**
	 * Ajout d'une durée au datetime fourni
	 * @param amount
	 * @param unit
	 */
	public plus(amount: number, unit: DateTimeUnit = DateUnit.day): LocalDateTime {
		return new LocalDateTime(this._date.plus({ [unit]: amount }))
	}

	public toJSON(): string {
		return this._date.toISO({ includeOffset: false }) as string
	}

	public get jsDate() {
		return new Date(this._safe(this._date.setZone(Temporal.TIMEZONE)).toISO())
	}
}
